<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useMediaQuery } from '@vueuse/core'
import emblaCarouselVue from 'embla-carousel-vue'
import CarouselChevron from './CarouselChevron.vue'

const props = defineProps({
    items: {
        type: Array,
        required: true,
    },
    itemKey: {
        type: String,
        default: 'id',
    },
    options: {
        type: Object,
        default: () => ({}),
    },
    slidesPerView: {
        type: Number,
        default: 1,
    },
    /**
     * Optional sm (640px) step; falls back to slidesPerView when not set.
     */
    slidesPerViewSm: {
        type: Number,
        default: null,
    },
    slidesPerViewMd: {
        type: Number,
        default: 3,
    },
    gap: {
        type: String,
        default: '1.5rem',
    },
    showArrows: {
        type: Boolean,
        default: true,
    },
    arrowSize: {
        type: String,
        default: 'md',
    },
    /**
     * overlay = chevrons on top of slides (banner style); beside = gutters + circular chevrons
     */
    arrowPlacement: {
        type: String,
        default: 'beside',
        validator: (arrowPlacement) => ['beside', 'overlay'].includes(arrowPlacement),
    },
    showDots: {
        type: Boolean,
        default: false,
    },
    /**
     * Auto-advance interval in ms. Null/0 disables. Minimum effective delay is 5000 when set.
     */
    autoplayDelay: {
        type: Number,
        default: null,
    },
})

const isSmallScreen = useMediaQuery('(min-width: 640px)')
const isMediumScreen = useMediaQuery('(min-width: 768px)')

const visibleSlides = computed(() => {
    if (isMediumScreen.value) {
        return props.slidesPerViewMd
    }

    if (props.slidesPerViewSm && isSmallScreen.value) {
        return props.slidesPerViewSm
    }

    return props.slidesPerView
})

const resolvedAutoplayDelay = computed(() => {
    if (! props.autoplayDelay || props.autoplayDelay <= 0) {
        return null
    }

    return Math.max(props.autoplayDelay, 5000)
})

const useOverlayArrows = computed(() => (
    props.showArrows && props.arrowPlacement === 'overlay'
))

const useBesideArrows = computed(() => (
    props.showArrows && props.arrowPlacement === 'beside'
))

// Equal share of the viewport, e.g. 3 slides => each is ~33.33% wide
const slideStyle = computed(() => ({
    flex: `0 0 ${100 / visibleSlides.value}%`,
    minWidth: 0,
    paddingLeft: props.gap,
}))

// Offset the first slide's left padding so the row still lines up with the edge
const containerStyle = computed(() => ({
    marginLeft: `-${props.gap}`,
}))

const emblaOptions = computed(() => ({
    align: 'start',
    containScroll: 'trimSnaps',
    slidesToScroll: 'auto',
    loop: false,
    ...props.options,
}))

const [emblaRef, emblaApi] = emblaCarouselVue(emblaOptions)

const canScrollPrev = ref(false)
const canScrollNext = ref(false)
const selectedIndex = ref(0)
const scrollSnapCount = ref(0)
const isAutoplayPaused = ref(false)

let autoplayIntervalId = null

const updateScrollState = () => {
    canScrollPrev.value = emblaApi.value?.canScrollPrev() ?? false
    canScrollNext.value = emblaApi.value?.canScrollNext() ?? false
    selectedIndex.value = emblaApi.value?.selectedScrollSnap() ?? 0
    scrollSnapCount.value = emblaApi.value?.scrollSnapList().length ?? 0
}

const clearAutoplayTimer = () => {
    if (autoplayIntervalId !== null) {
        clearInterval(autoplayIntervalId)
        autoplayIntervalId = null
    }
}

const startAutoplayTimer = () => {
    clearAutoplayTimer()

    if (
        ! resolvedAutoplayDelay.value
        || props.items.length < 2
        || isAutoplayPaused.value
        || ! emblaApi.value
    ) {
        return
    }

    autoplayIntervalId = setInterval(() => {
        emblaApi.value?.scrollNext()
    }, resolvedAutoplayDelay.value)
}

const pauseAutoplay = () => {
    isAutoplayPaused.value = true
    clearAutoplayTimer()
}

const resumeAutoplay = () => {
    isAutoplayPaused.value = false
    startAutoplayTimer()
}

const onFocusOut = (focusEvent) => {
    const carouselRoot = focusEvent.currentTarget
    const nextFocusedElement = focusEvent.relatedTarget

    if (nextFocusedElement instanceof Node && carouselRoot.contains(nextFocusedElement)) {
        return
    }

    resumeAutoplay()
}

const scrollPrev = (clickEvent) => {
    clickEvent?.preventDefault?.()
    clickEvent?.stopPropagation?.()
    emblaApi.value?.scrollPrev()
}

const scrollNext = (clickEvent) => {
    clickEvent?.preventDefault?.()
    clickEvent?.stopPropagation?.()
    emblaApi.value?.scrollNext()
}

const scrollTo = (snapIndex, clickEvent) => {
    clickEvent?.preventDefault?.()
    clickEvent?.stopPropagation?.()
    emblaApi.value?.scrollTo(snapIndex)
}

const reInitCarousel = async () => {
    await nextTick()
    emblaApi.value?.reInit()
    updateScrollState()
    startAutoplayTimer()
}

watch(emblaApi, (carouselApi) => {
    if (! carouselApi) {
        return
    }

    updateScrollState()
    carouselApi.on('select', updateScrollState)
    carouselApi.on('reInit', updateScrollState)
    carouselApi.on('resize', updateScrollState)
    startAutoplayTimer()
})

watch(
    [visibleSlides, () => props.gap, () => props.items.length, resolvedAutoplayDelay],
    () => {
        reInitCarousel()
    },
)

onBeforeUnmount(() => {
    clearAutoplayTimer()
})
</script>

<template>
    <div
        @mouseenter="pauseAutoplay"
        @mouseleave="resumeAutoplay"
        @focusin="pauseAutoplay"
        @focusout="onFocusOut"
    >
        <div class="relative">
            <button
                v-if="useBesideArrows"
                type="button"
                class="absolute left-0 top-1/2 z-20 -translate-y-1/2 disabled:opacity-30 disabled:cursor-not-allowed"
                :disabled="! canScrollPrev"
                aria-label="Previous slides"
                @click="scrollPrev"
            >
                <CarouselChevron
                    icon="fa fa-chevron-left"
                    :size="arrowSize"
                />
            </button>

            <button
                v-if="useOverlayArrows"
                type="button"
                class="absolute left-2 top-1/2 z-20 -translate-y-1/2 p-2 text-white drop-shadow-md disabled:opacity-30 disabled:cursor-not-allowed"
                :disabled="! canScrollPrev"
                aria-label="Previous slides"
                @click="scrollPrev"
            >
                <fa-icon
                    icon="fa fa-chevron-left"
                    class="text-2xl"
                />
            </button>

            <div
                ref="emblaRef"
                class="overflow-hidden"
                :class="[
                    useBesideArrows ? 'mx-10 pt-2 pb-6' : 'pt-2 pb-6',
                ]"
            >
                <div
                    class="flex"
                    :style="containerStyle"
                >
                    <div
                        v-for="(item, index) in items"
                        :key="item?.[itemKey] ?? index"
                        :style="slideStyle"
                    >
                        <slot :item="item" :index="index" />
                    </div>
                </div>
            </div>

            <button
                v-if="useBesideArrows"
                type="button"
                class="absolute right-0 top-1/2 z-20 -translate-y-1/2 disabled:opacity-30 disabled:cursor-not-allowed"
                :disabled="! canScrollNext"
                aria-label="Next slides"
                @click="scrollNext"
            >
                <CarouselChevron
                    icon="fa fa-chevron-right"
                    :size="arrowSize"
                />
            </button>

            <button
                v-if="useOverlayArrows"
                type="button"
                class="absolute right-2 top-1/2 z-20 -translate-y-1/2 p-2 text-white drop-shadow-md disabled:opacity-30 disabled:cursor-not-allowed"
                :disabled="! canScrollNext"
                aria-label="Next slides"
                @click="scrollNext"
            >
                <fa-icon
                    icon="fa fa-chevron-right"
                    class="text-2xl"
                />
            </button>
        </div>

        <div
            v-if="showDots && scrollSnapCount > 1"
            class="flex justify-center items-center gap-3 mt-3"
            role="tablist"
            aria-label="Carousel pagination"
        >
            <button
                v-for="snapIndex in scrollSnapCount"
                :key="snapIndex"
                type="button"
                role="tab"
                class="w-4 h-4 rounded-full border-2 border-ll-red transition-colors"
                :class="selectedIndex === snapIndex - 1 ? 'bg-ll-red' : 'bg-transparent'"
                :aria-label="`Go to slide ${snapIndex}`"
                :aria-selected="selectedIndex === snapIndex - 1"
                @click="scrollTo(snapIndex - 1, $event)"
            />
        </div>
    </div>
</template>
